import { useMemo, type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router";
import { AgentCard } from "../components/workforce/AgentCard";
import { WorkforceEmptyState } from "../components/workforce/WorkforceEmptyState";
import { WorkforceTeamView } from "../components/workforce/WorkforceTeamView";
import {
  WorkforceFilterChips,
  type WorkforceFilter,
} from "../components/workforce/WorkforceFilterChips";
import { WorkforceSubhead } from "../components/workforce/WorkforceSubhead";
import { useThemedLabels } from "../hooks/use-themed-labels";
import { useChatScope } from "../hooks/use-chat-scope";
import { useWorkforceStatus, type WorkforceStatusMap } from "../hooks/use-workforce-status";
import { useListTree, useWorkspaceInfo } from "../../lib/hooks/use-kuvio";
import { useAuth } from "../../lib/hooks/use-auth";
import type { LibraryNode, WorkforceStatusKind } from "../../lib/types";

type WorkforceLens = "roster" | "team";

function normaliseFilter(raw: string | null): WorkforceFilter {
  return raw === "working" || raw === "awaiting" || raw === "available" ? raw : "all";
}

function normaliseLens(raw: string | null): WorkforceLens {
  return raw === "team" ? "team" : "roster";
}

function statusRank(kind: WorkforceStatusKind): number {
  return kind === "working" ? 0 : kind === "awaiting" ? 1 : 2;
}

function sortAgents(list: LibraryNode[], statusMap: WorkforceStatusMap): LibraryNode[] {
  return [...list].sort((a, b) => {
    const sa = statusMap[a.id]?.status ?? "available";
    const sb = statusMap[b.id]?.status ?? "available";
    const rank = statusRank(sa) - statusRank(sb);
    if (rank !== 0) return rank;
    return a.name.localeCompare(b.name);
  });
}

export function WorkforcePage() {
  const { currentWorkspaceId } = useAuth();
  const tree = useListTree(currentWorkspaceId ?? undefined);
  const workspace = useWorkspaceInfo();
  const labels = useThemedLabels();
  const navigate = useNavigate();
  const location = useLocation();

  void workspace.data?.root_node_id;

  const agents = useMemo<LibraryNode[]>(
    () => (tree.data ?? []).filter((n) => n.type === "agent" && !n.archived_at),
    [tree.data],
  );
  const agentIds = useMemo(() => agents.map((a) => a.id), [agents]);

  const statusQuery = useWorkforceStatus(agentIds, agents.length > 0);
  const statusMap = statusQuery.data ?? {};

  const pageContext = useMemo(
    () => ({
      kind: "workforce",
      name: "Workforce",
      snippet: "The user is viewing the workforce — every agent in this workspace as a team.",
    }),
    [],
  );
  useChatScope({ kind: "workforce", label: "Workforce" }, pageContext, ["Workforce"]);

  const hashParams = new URLSearchParams(location.hash.replace(/^#/, ""));
  const filter = normaliseFilter(hashParams.get("filter"));
  const lens = normaliseLens(hashParams.get("lens"));

  function updateHash(next: { filter?: WorkforceFilter; lens?: WorkforceLens }) {
    const p = new URLSearchParams(location.hash.replace(/^#/, ""));
    if (next.filter) p.set("filter", next.filter);
    if (next.lens) p.set("lens", next.lens);
    navigate(`/workforce#${p.toString()}`);
  }

  const counts = useMemo(() => {
    let working = 0;
    let awaiting = 0;
    let available = 0;
    for (const a of agents) {
      const s = statusMap[a.id]?.status ?? "available";
      if (s === "working") working += 1;
      else if (s === "awaiting") awaiting += 1;
      else available += 1;
    }
    return { all: agents.length, working, awaiting, available };
  }, [agents, statusMap]);

  const visible = useMemo(() => {
    const filtered = agents.filter((a) => {
      if (filter === "all") return true;
      return (statusMap[a.id]?.status ?? "available") === filter;
    });
    return sortAgents(filtered, statusMap);
  }, [agents, filter, statusMap]);

  if (!tree.isLoading && !tree.error && agents.length === 0) {
    return <WorkforceEmptyState labels={labels} />;
  }

  return (
    <div style={{ maxWidth: 1000 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0, color: "var(--color-text-primary)" }}>
            Workforce
          </h1>
          <WorkforceSubhead
            total={counts.all}
            working={counts.working}
            awaiting={counts.awaiting}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          {/* Roster | Team lens — Roster is the card grid; Team is the org pyramid. */}
          <div style={lensToggle} role="tablist" aria-label="Workforce lens" data-testid="workforce-lens-toggle">
            <LensButton label="Roster" active={lens === "roster"} onClick={() => updateHash({ lens: "roster" })} />
            <LensButton label="Team" active={lens === "team"} onClick={() => updateHash({ lens: "team" })} />
          </div>
          <button type="button" onClick={() => navigate("/hire")} style={hireButton}>
            <i className="ti ti-plus" style={{ fontSize: 14 }} aria-hidden="true" />
            Hire
          </button>
        </div>
      </div>

      {tree.isLoading ? (
        <div style={grid}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={skeletonCard} />
          ))}
        </div>
      ) : tree.error ? (
        <div style={{ padding: 24, textAlign: "center", color: "var(--color-text-tertiary)" }}>
          Couldn't load your workforce.{" "}
          <button onClick={() => tree.refetch()} style={linkButton}>Retry</button>
        </div>
      ) : lens === "team" ? (
        <WorkforceTeamView agents={agents} onOpen={(id) => navigate(`/agents/${id}`)} />
      ) : (
        <>
          <WorkforceFilterChips
            active={filter}
            counts={counts}
            onChange={(f) => updateHash({ filter: f })}
          />

          {visible.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--color-text-tertiary)", fontSize: 13 }}>
              No agents match this filter.
            </div>
          ) : (
            <div style={grid}>
              {visible.map((agent) => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  status={statusMap[agent.id]}
                  onOpen={(id) => navigate(`/agents/${id}`)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function LensButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{ ...lensBtn, ...(active ? lensBtnActive : null) }}
      data-testid={`workforce-lens-${label.toLowerCase()}`}
    >
      {label}
    </button>
  );
}

const grid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
  gap: 12,
};

const lensToggle: CSSProperties = {
  display: "inline-flex",
  gap: 2,
  padding: 2,
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
};

const lensBtn: CSSProperties = {
  padding: "5px 12px",
  fontSize: 12,
  fontWeight: 400,
  border: "none",
  borderRadius: "var(--border-radius-sm)",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const lensBtnActive: CSSProperties = {
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontWeight: 500,
};

const skeletonCard: CSSProperties = {
  background: "var(--color-background-secondary)",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-lg)",
  height: 220,
  animation: "pulse 1.4s ease-in-out infinite",
};

const hireButton: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  flexShrink: 0,
  fontSize: 13,
  fontWeight: 500,
  padding: "6px 12px",
  borderRadius: "var(--border-radius-md)",
  border: "none",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const linkButton: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-info, var(--color-text-secondary))",
  background: "none",
  border: "none",
  cursor: "pointer",
};
